import type { JSX } from "react";
import { LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FramnaBidLogoIcon } from "./framnabid-logo";
import type { NavSlug } from "../lib/app-routing";
import type { NavGroup } from "@/features/app/types";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarSeparator,
  useSidebar
} from "@/components/ui/sidebar";

type AppSidebarProps = {
  navGroups: NavGroup[];
  activeItem: NavSlug;
  onSelectItem: (id: NavSlug) => void;
  themeLabel: string;
  onToggleTheme: () => void;
  userLabel: string;
  onSignOut: () => void;
  ThemeIcon: (props: { className?: string }) => JSX.Element;
};

export const AppSidebar = ({
  navGroups,
  activeItem,
  onSelectItem,
  themeLabel,
  onToggleTheme,
  userLabel,
  onSignOut,
  ThemeIcon
}: AppSidebarProps): JSX.Element => {
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";

  return (
    <Sidebar variant="inset" collapsible="icon">
      <SidebarHeader className={isCollapsed ? "px-2 py-6" : "px-4 py-6"}>
        <div className="flex items-center gap-3 group-data-[collapsible=icon]:justify-center">
          <FramnaBidLogoIcon className={isCollapsed ? "size-14 shrink-0" : "size-9 shrink-0"} />
          <div className="min-w-0 group-data-[collapsible=icon]:hidden">
            <p className="font-editorial text-sidebar-foreground text-[1.6rem] leading-none">
              FramnaBid
            </p>
          </div>
        </div>
      </SidebarHeader>
      <SidebarSeparator />
      <SidebarContent>
        {navGroups.map((group) => (
          <SidebarGroup key={group.id}>
            <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
            <SidebarMenu>
              {group.items.map((item) => (
                <SidebarMenuItem key={item.id}>
                  <SidebarMenuButton
                    isActive={activeItem === item.id}
                    tooltip={item.label}
                    onClick={() => onSelectItem(item.id)}
                  >
                    <item.icon className="size-4" />
                    <span>{item.label}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroup>
        ))}
      </SidebarContent>
      <SidebarFooter className="border-t border-sidebar-border/80 pt-3">
        <div className="flex justify-end">
          <Button
            variant="ghost"
            size="icon"
            className="size-8 rounded-full border border-white/15 bg-black/10 text-white hover:bg-black/20"
            aria-label={themeLabel}
            onClick={onToggleTheme}
          >
            <ThemeIcon className="size-4" />
            <span className="sr-only">{themeLabel}</span>
          </Button>
        </div>
        {isCollapsed ? (
          <div className="flex justify-center">
            <Button
              variant="ghost"
              size="icon"
              className="size-9 rounded-full border border-white/15 bg-black/10 text-white hover:bg-black/20"
              aria-label="Sign out"
              onClick={onSignOut}
            >
              <LogOut className="size-4" />
            </Button>
          </div>
        ) : (
          <div className="rounded-xl border border-white/15 bg-black/10 px-3 py-2 text-sm text-white">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[0.62rem] uppercase tracking-[0.22em] text-white/72">Signed in as</p>
                <p className="truncate pt-1">{userLabel}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-auto shrink-0 rounded-full border border-white/15 bg-black/10 px-2.5 py-1 text-xs font-medium text-white hover:bg-black/20"
                onClick={onSignOut}
              >
                <span>Sign out</span>
              </Button>
            </div>
          </div>
        )}
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
};
